import { Building2, HardHat } from "lucide-react";
import { useRoles } from "./RolePicker";

const ITEMS = [
  ["retail_store", "Trained staff for supermarkets and stores: trolley boys, packers, shelf stockers, cleaners and floor greeters to keep your aisles running smoothly."],
  ["cinema", "Gatekeepers, ushers, hall cleaners, concession and box-office staff to run cinema and theatre shows on time."],
  ["factory_garment", "Helpers, machine operators, tailors, packers and line workers for garment mills, factories and manufacturing units."],
  ["logistics", "Loaders, pickers, packers, inventory and warehouse staff to keep your supply chain moving every shift."],
  ["hotel", "Waiters and cooks for hotels, restaurants and events, verified and ready to serve your guests."],
  ["housekeeping", "Housekeeping staff, deep cleaning, sofa/carpet cleaning, water tank cleaning and AC service teams for homes and offices."],
  ["book_service", "Book a cleaner, construction labour, water tank cleaner or AC technician online. Pay a small advance and we send verified staff."],
  ["security_facility", "Trained security guards, supervisors and facility staff for malls, factories, warehouses and offices."],
  ["hospital", "Nurses and hospital cleaners for clinics and hospitals, with hygiene-trained and verified staff."],
];

const DeptCard = ({ dept, text, index, onPick }) => (
  <article data-testid={`roles-dept-${dept.value}`} className="flex flex-col rounded-2xl border border-white/10 bg-[#0F172A] p-6">
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-heading text-lg font-bold text-white">{dept.label}</h3>
      <span className="shrink-0 rounded-full bg-orange-500 px-2 py-0.5 font-mono text-xs font-bold text-white">{String(index + 1).padStart(2, "0")}</span>
    </div>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{text}</p>
    <div className="mt-5 flex gap-4 text-sm font-semibold">
      <button type="button" data-testid={`roles-hire-${dept.value}`} onClick={() => onPick("employer", dept.value, dept.roles[0])}
        className="flex items-center gap-1.5 text-orange-500 transition-colors hover:text-orange-600">
        <Building2 className="h-4 w-4" /> Hire staff
      </button>
      <button type="button" data-testid={`roles-apply-${dept.value}`} onClick={() => onPick("worker", dept.value, dept.roles[0])}
        className="flex items-center gap-1.5 text-slate-300 transition-colors hover:text-orange-500">
        <HardHat className="h-4 w-4" /> Apply for job
      </button>
    </div>
  </article>
);

export const RolesCatalog = ({ onPick }) => {
  const departments = useRoles();
  const byValue = Object.fromEntries(departments.map((d) => [d.value, d]));
  const items = ITEMS.filter(([v]) => byValue[v]);

  return (
    <section id="roles" className="scroll-mt-20 border-t border-white/5 bg-[#0B1120] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="eyebrow">Roles we supply</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white lg:text-4xl" data-testid="roles-heading">
              Staff for every department.
            </h2>
          </div>
          <p className="max-w-sm text-slate-400">Pick a department to hire staff or apply for a job. The form opens with it already selected.</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map(([v, text], i) => <DeptCard key={v} dept={byValue[v]} text={text} index={i} onPick={onPick} />)}
        </div>
      </div>
    </section>
  );
};
